import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { InstallationGuide } from "@/components/InstallationGuide";
import {
  ChevronLeft,
  Package,
  Settings,
  Users,
  BarChart3,
  Info,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Power,
  Trash2,
  RefreshCw,
  Loader2,
  Download,
  BookOpen,
  Plus,
  ArrowUpCircle,
  History,
  Sparkles,
  ExternalLink,
} from "lucide-react";

type Tab = "overview" | "bobs" | "config" | "analytics" | "settings";

interface BoB {
  id: string;
  name: string;
  description: string;
  category: string;
  sop_id: string;
  version: string;
  is_enabled: boolean;
  last_used?: string | null;
  // T151: Update detection fields
  latest_version?: string;
  has_update?: boolean;
  previous_version?: string;
  can_rollback?: boolean;
}

interface BoBUpdate {
  bob_name: string;
  current_version: string;
  latest_version: string;
  changelog_url?: string;
}

export function InstanceDetail() {
  const { instanceId } = useParams<{ instanceId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>("overview");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showInstallGuide, setShowInstallGuide] = useState(false);
  const [showChangelogModal, setShowChangelogModal] = useState<string | null>(null);
  const [upgradingBob, setUpgradingBob] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // T131: Fetch instance details
  const { data, isLoading, error } = useQuery({
    queryKey: ["instance-detail", instanceId],
    queryFn: async () => {
      const instance = await api.getInstanceDetail(instanceId!);
      return { instance };
    },
    enabled: !!instanceId,
  });

  // T131: Fetch BoBs for instance
  const { data: bobsData, isLoading: bobsLoading } = useQuery({
    queryKey: ["instance-bobs", instanceId],
    queryFn: async () => {
      return await api.getInstanceBoBs(instanceId!);
    },
    enabled: !!instanceId && activeTab === "bobs",
  });

  // T133: Sync BoB registry mutation
  const syncBoBsMutation = useMutation({
    mutationFn: async () => {
      return await api.syncBoBRegistry(instanceId!);
    },
    onSuccess: () => {
      // Refetch BoBs after sync
      queryClient.invalidateQueries({
        queryKey: ["instance-bobs", instanceId],
      });
    },
  });

  // Toggle BoB enabled state
  const toggleBoBMutation = useMutation({
    mutationFn: async ({ bobId, enabled }: { bobId: string; enabled: boolean }) => {
      return await api.toggleBoB(instanceId!, bobId, enabled);
    },
    onSuccess: () => {
      // Refetch BoBs after toggle
      queryClient.invalidateQueries({
        queryKey: ["instance-bobs", instanceId],
      });
    },
  });

  // Delete instance mutation
  const deleteInstanceMutation = useMutation({
    mutationFn: async () => {
      return await api.deleteInstance(instanceId!);
    },
    onSuccess: () => {
      // Navigate back to instances list
      navigate("/client/instances");
    },
  });

  // PROD-P0-002: Download connector mutation
  const downloadConnectorMutation = useMutation({
    mutationFn: async () => {
      return await api.downloadConnectorInstaller(instanceId!);
    },
    onSuccess: (data) => {
      // Open presigned URL in new tab
      if (data.download_url) {
        window.open(data.download_url, '_blank');
      }
    },
  });

  // T142-T144: Check for BoB updates
  const { data: updatesData } = useQuery({
    queryKey: ["instance-bob-updates", instanceId],
    queryFn: async () => {
      return await api.checkBoBUpdates(instanceId!);
    },
    enabled: !!instanceId && activeTab === "bobs",
    staleTime: 60000, // Check every minute
  });

  // T145: Upgrade single BoB mutation
  const upgradeBoBMutation = useMutation({
    mutationFn: async (bobName: string) => {
      setUpgradingBob(bobName);
      return await api.upgradeBob(instanceId!, bobName);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["instance-bobs", instanceId] });
      queryClient.invalidateQueries({ queryKey: ["instance-bob-updates", instanceId] });
      setUpgradingBob(null);
    },
    onError: () => {
      setUpgradingBob(null);
    },
  });

  // T147: Rollback BoB mutation
  const rollbackBoBMutation = useMutation({
    mutationFn: async (bobName: string) => {
      return await api.rollbackBob(instanceId!, bobName);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["instance-bobs", instanceId] });
      queryClient.invalidateQueries({ queryKey: ["instance-bob-updates", instanceId] });
    },
  });

  // T156: Upgrade all BoBs mutation
  const upgradeAllBoBsMutation = useMutation({
    mutationFn: async () => {
      return await api.upgradeAllBobs(instanceId!);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["instance-bobs", instanceId] });
      queryClient.invalidateQueries({ queryKey: ["instance-bob-updates", instanceId] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-slate-300">Loading instance details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-red-400">
          Error loading instance: {(error as Error).message}
        </div>
      </div>
    );
  }

  const instance = data?.instance;
  const bobs = bobsData?.bobs || [];

  if (!instance) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-slate-300">Instance not found</div>
      </div>
    );
  }

  const getHealthIcon = (status: typeof instance.health_status) => {
    switch (status) {
      case "healthy":
        return <CheckCircle2 className="h-5 w-5 text-emerald-400" />;
      case "degraded":
        return <AlertCircle className="h-5 w-5 text-yellow-400" />;
      case "down":
        return <XCircle className="h-5 w-5 text-red-400" />;
    }
  };

  const tabs = [
    { id: "overview" as Tab, label: "Overview", icon: Info },
    { id: "bobs" as Tab, label: "BoBs", icon: Package },
    { id: "config" as Tab, label: "Configuration", icon: Settings },
    { id: "analytics" as Tab, label: "Analytics", icon: BarChart3 },
    { id: "settings" as Tab, label: "Settings", icon: Settings },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={() => navigate("/client/dashboard")}
          className="flex items-center gap-2 text-sm text-slate-300 hover:text-slate-300 transition-colors mb-4"
        >
          <ChevronLeft className="h-4 w-4" />
          Back to Dashboard
        </button>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-100">
              {instance.name}
            </h1>
            <p className="mt-1 text-sm text-slate-300">{instance.odoo_url}</p>
          </div>
          <div className="flex items-center gap-2">
            {getHealthIcon(instance.health_status)}
            <span className="text-sm font-medium text-slate-300 capitalize">
              {instance.health_status}
            </span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-700">
        <nav className="flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? "border-blue-500 text-blue-400"
                    : "border-transparent text-slate-300 hover:text-slate-300"
                }`}
              >
                <Icon className="h-5 w-5" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-slate-700 p-6">
        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-slate-100">
              Instance Overview
            </h3>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-300">Status</span>
                  <span className="text-slate-100 font-medium capitalize">
                    {instance.status}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-300">Health</span>
                  <span className="text-emerald-400 font-medium capitalize">
                    {instance.health_status}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-300">Tier</span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 capitalize">
                    {instance.tier}
                  </span>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-300">Active BoBs</span>
                  <span className="text-slate-100 font-medium">
                    {bobs.filter((b) => b.is_enabled).length} / {bobs.length}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-300">Created</span>
                  <span className="text-slate-100 font-medium">
                    {new Date(instance.created_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-300">URL</span>
                  <a
                    href={instance.odoo_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300 font-medium"
                  >
                    Open →
                  </a>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-slate-700">
              <h4 className="text-lg font-semibold text-slate-100 mb-4">
                Quick Actions
              </h4>
              <div className="flex gap-3">
                <button className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 transition-colors">
                  <Power className="h-4 w-4" />
                  Restart Instance
                </button>
                <button className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-700 text-slate-300 border border-slate-600 hover:bg-slate-600 transition-colors">
                  <Package className="h-4 w-4" />
                  Manage BoBs
                </button>
                <button
                  onClick={() => downloadConnectorMutation.mutate()}
                  disabled={downloadConnectorMutation.isPending}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500/10 text-blue-400 border border-blue-500/20 hover:bg-blue-500/20 transition-colors disabled:opacity-50"
                  aria-label="Download connector installer"
                >
                  {downloadConnectorMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : (
                    <Download className="h-4 w-4" aria-hidden="true" />
                  )}
                  Download Connector
                </button>
                <button
                  onClick={() => setShowInstallGuide(true)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-500/10 text-purple-400 border border-purple-500/20 hover:bg-purple-500/20 transition-colors"
                  aria-label="View installation guide"
                >
                  <BookOpen className="h-4 w-4" aria-hidden="true" />
                  Installation Guide
                </button>
              </div>
            </div>
          </div>
        )}

        {/* BoBs Tab */}
        {activeTab === "bobs" && (
          <div className="space-y-6">
            {/* T148: Header with Add BoB and Update All buttons */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h3 className="text-xl font-semibold text-slate-100">
                  Enabled BoBs
                </h3>
                <p className="text-sm text-slate-300 mt-1">
                  {bobsData?.enabled_count || 0} of {bobsData?.total || 0} BoBs
                  enabled
                  {updatesData?.updates?.length > 0 && (
                    <span className="ml-2 text-amber-400">
                      • {updatesData.updates.length} update{updatesData.updates.length !== 1 ? "s" : ""} available
                    </span>
                  )}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {/* T148: Add BoB button */}
                <Link
                  to={`/client/instances/${instanceId}/bobs`}
                  className="px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-medium transition-colors flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  Add BoB
                </Link>
                {/* T156: Update All button */}
                {updatesData?.updates?.length > 0 && (
                  <button
                    onClick={() => upgradeAllBoBsMutation.mutate()}
                    disabled={upgradeAllBoBsMutation.isPending}
                    className="px-4 py-2 rounded-lg bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    {upgradeAllBoBsMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    ) : (
                      <Sparkles className="h-4 w-4" aria-hidden="true" />
                    )}
                    Update All
                  </button>
                )}
                <button
                  onClick={() => syncBoBsMutation.mutate()}
                  disabled={syncBoBsMutation.isPending}
                  className="px-4 py-2 rounded-lg bg-slate-600 hover:bg-slate-500 text-white font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
                  title="Sync BoB Registry"
                >
                  <RefreshCw
                    className={`h-4 w-4 ${syncBoBsMutation.isPending ? "animate-spin" : ""}`}
                  />
                  Sync Registry
                </button>
              </div>
            </div>

            {bobs.length === 0 ? (
              <div className="text-center py-12">
                <Package className="mx-auto h-12 w-12 text-slate-600" />
                <p className="mt-2 text-slate-300">No BoBs installed yet</p>
                <Link
                  to={`/client/instances/${instanceId}/bobs`}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white transition-colors"
                >
                  <Package className="h-4 w-4" aria-hidden="true" />
                  Browse BoB Marketplace
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {bobs.map((bob) => {
                  const bobUpdate = updatesData?.updates?.find(
                    (u: BoBUpdate) => u.bob_name === bob.name
                  );
                  const isUpgrading = upgradingBob === bob.name;

                  return (
                  <div
                    key={bob.id}
                    className={`flex items-center justify-between p-4 bg-slate-700/30 rounded-lg border ${
                      bobUpdate ? "border-amber-500/30" : "border-slate-600"
                    }`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-slate-100">{bob.name}</h4>
                        <span className="text-xs px-1.5 py-0.5 rounded bg-slate-600 text-slate-400">
                          v{bob.version}
                        </span>
                        {/* T151: Update Available badge */}
                        {bobUpdate && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20 flex items-center gap-1">
                            <ArrowUpCircle className="h-3 w-3" aria-hidden="true" />
                            v{bobUpdate.latest_version} available
                          </span>
                        )}
                        {/* T154: Can rollback indicator */}
                        {bob.can_rollback && bob.previous_version && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-slate-600 text-slate-400 flex items-center gap-1">
                            <History className="h-3 w-3" aria-hidden="true" />
                            Can rollback to v{bob.previous_version}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-300 mt-1">
                        {bob.description}
                      </p>
                      <div className="mt-2 flex items-center gap-2">
                        <span className="text-xs px-2 py-0.5 rounded bg-slate-600 text-slate-300">
                          {bob.category}
                        </span>
                        {bob.last_used && (
                          <span className="text-xs text-slate-500">
                            Last used:{" "}
                            {new Date(bob.last_used).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div className="flex items-center gap-3 ml-4">
                      {/* T152-T153: Upgrade button with changelog */}
                      {bobUpdate && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setShowChangelogModal(bob.name)}
                            className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
                            aria-label={`View changelog for ${bob.name}`}
                          >
                            <ExternalLink className="h-3 w-3" aria-hidden="true" />
                            Changelog
                          </button>
                          <button
                            onClick={() => upgradeBoBMutation.mutate(bob.name)}
                            disabled={isUpgrading || upgradeBoBMutation.isPending}
                            className="px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium transition-colors flex items-center gap-1 disabled:opacity-50"
                            aria-label={`Upgrade ${bob.name} to version ${bobUpdate.latest_version}`}
                          >
                            {isUpgrading ? (
                              <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                            ) : (
                              <ArrowUpCircle className="h-3 w-3" aria-hidden="true" />
                            )}
                            Upgrade
                          </button>
                        </div>
                      )}

                      {/* T154: Rollback button */}
                      {bob.can_rollback && bob.previous_version && !bobUpdate && (
                        <button
                          onClick={() => rollbackBoBMutation.mutate(bob.name)}
                          disabled={rollbackBoBMutation.isPending}
                          className="px-3 py-1.5 rounded-lg bg-slate-600 hover:bg-slate-500 text-slate-300 text-sm font-medium transition-colors flex items-center gap-1 disabled:opacity-50"
                          aria-label={`Rollback ${bob.name} to version ${bob.previous_version}`}
                        >
                          {rollbackBoBMutation.isPending ? (
                            <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                          ) : (
                            <History className="h-3 w-3" aria-hidden="true" />
                          )}
                          Rollback
                        </button>
                      )}

                      {/* Enable/Disable toggle */}
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={bob.is_enabled}
                          onChange={() => {
                            toggleBoBMutation.mutate({
                              bobId: bob.id,
                              enabled: !bob.is_enabled,
                            });
                          }}
                          disabled={toggleBoBMutation.isPending}
                          className="sr-only peer"
                          aria-label={`Toggle ${bob.name}`}
                        />
                        <div className="w-11 h-6 bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500 peer-disabled:opacity-50 peer-disabled:cursor-not-allowed"></div>
                      </label>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Configuration Tab */}
        {activeTab === "config" && (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-slate-100">
              Configuration
            </h3>
            <p className="text-slate-300">
              Instance configuration settings will be displayed here.
            </p>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === "analytics" && (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-slate-100">Analytics</h3>
            <p className="text-slate-300">
              Usage analytics and metrics will be displayed here.
            </p>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === "settings" && (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-slate-100">
              Instance Settings
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Instance Name
                </label>
                <input
                  type="text"
                  defaultValue={instance.name}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-100 focus:outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Odoo URL
                </label>
                <input
                  type="url"
                  defaultValue={instance.odoo_url}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-100 focus:outline-none focus:border-blue-500"
                />
              </div>

              <div className="pt-4 border-t border-slate-700">
                <h4 className="text-lg font-semibold text-red-400 mb-4">
                  Danger Zone
                </h4>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete Instance
                </button>
                <p className="mt-2 text-xs text-slate-500">
                  This action cannot be undone. All data will be permanently
                  deleted.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowDeleteConfirm(false);
            }
          }}
        >
          <div className="bg-slate-800 rounded-lg p-6 max-w-md w-full mx-4 border border-slate-700 shadow-xl">
            <h3 className="text-xl font-semibold text-slate-100 mb-2">
              Delete Instance?
            </h3>
            <p className="text-slate-300 mb-6">
              Are you sure you want to delete{" "}
              <span className="font-medium text-slate-200">{instance.name}</span>?
              This action cannot be undone and all data will be permanently deleted.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleteInstanceMutation.isPending}
                className="px-4 py-2 rounded-lg bg-slate-700 text-slate-300 hover:bg-slate-600 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteInstanceMutation.mutate()}
                disabled={deleteInstanceMutation.isPending}
                className="px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {deleteInstanceMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    Deleting...
                  </>
                ) : (
                  "Delete Instance"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PROD-P0-005: Installation Guide Modal */}
      <InstallationGuide
        isOpen={showInstallGuide}
        onClose={() => setShowInstallGuide(false)}
        instanceId={instanceId}
      />

      {/* T152: Changelog Modal */}
      {showChangelogModal && (
        <ChangelogModal
          bobName={showChangelogModal}
          onClose={() => setShowChangelogModal(null)}
          onUpgrade={() => {
            upgradeBoBMutation.mutate(showChangelogModal);
            setShowChangelogModal(null);
          }}
        />
      )}
    </div>
  );
}

/**
 * T152: BoB Changelog Modal
 * Displays release notes for a BoB update
 */
interface ChangelogModalProps {
  bobName: string;
  onClose: () => void;
  onUpgrade: () => void;
}

function ChangelogModal({ bobName, onClose, onUpgrade }: ChangelogModalProps) {
  const { data: changelog, isLoading } = useQuery({
    queryKey: ["bob-changelog", bobName],
    queryFn: async () => {
      return await api.getBoBChangelog(bobName);
    },
  });

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="bg-slate-800 rounded-lg p-6 max-w-lg w-full mx-4 border border-slate-700 shadow-xl max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-slate-100">
            {bobName} Changelog
          </h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-300 transition-colors"
            aria-label="Close changelog"
          >
            <XCircle className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 text-blue-400 animate-spin" />
            </div>
          ) : changelog ? (
            <div className="space-y-4">
              {/* Version header */}
              <div className="flex items-center gap-2 text-emerald-400">
                <ArrowUpCircle className="h-4 w-4" />
                <span className="font-medium">
                  Version {changelog.version}
                </span>
                {changelog.released_at && (
                  <span className="text-xs text-slate-500">
                    • {new Date(changelog.released_at).toLocaleDateString()}
                  </span>
                )}
              </div>

              {/* Changes list */}
              {changelog.changes && changelog.changes.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-slate-300 mb-2">What's New</h4>
                  <ul className="space-y-2">
                    {changelog.changes.map((change: string, idx: number) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-slate-400">
                        <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span>{change}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Bug fixes */}
              {changelog.bug_fixes && changelog.bug_fixes.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-slate-300 mb-2">Bug Fixes</h4>
                  <ul className="space-y-2">
                    {changelog.bug_fixes.map((fix: string, idx: number) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-slate-400">
                        <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                        <span>{fix}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Breaking changes */}
              {changelog.breaking_changes && changelog.breaking_changes.length > 0 && (
                <div className="bg-red-900/20 border border-red-800 rounded-lg p-3">
                  <h4 className="text-sm font-medium text-red-400 mb-2">Breaking Changes</h4>
                  <ul className="space-y-2">
                    {changelog.breaking_changes.map((change: string, idx: number) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-red-300">
                        <XCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                        <span>{change}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <p className="text-slate-400 text-center py-8">
              No changelog available for this version.
            </p>
          )}
        </div>

        <div className="flex gap-3 justify-end mt-4 pt-4 border-t border-slate-700">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-slate-700 text-slate-300 hover:bg-slate-600 transition-colors"
          >
            Close
          </button>
          <button
            onClick={onUpgrade}
            className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-medium transition-colors flex items-center gap-2"
          >
            <ArrowUpCircle className="h-4 w-4" />
            Upgrade Now
          </button>
        </div>
      </div>
    </div>
  );
}
